/**
 * 새 작업 — 「작업 이름」(선택 칸 · `docs/backend-requests.csv` BR-94 · 2026-09-26).
 *
 * 🔴 **지금 서버는 이 이름을 받지 않는다.** `CreateBatchBody` 에 `name` 이 없고 pydantic 기본
 *    (`extra` 무시)이라 실어 보내도 422 없이 **조용히 버린다** — `Batch.name` 은 서버가
 *    「레시피 · 수행인 · 시각」으로 조립한다. ⊕ **2026-10-23 부터 칸은 비활성 + 사유이고 싣지 않는다**(`BATCH_NAME_ACCEPTED`).
 *    서버가 받는 날 그 플래그 하나를 켜면 이름을 싣고, 버려졌으면 그 사실을 말한다(`ignoredBatchName`).
 */

/**
 * 이름 길이 상한(글자 수). 🔸 **서버 상한이 아니다** — `batch.name` 은 `TEXT` 라 서버는 재지 않는다
 * (`db/migrations/0020_batch_name.sql`). 한 줄 표시에 들어갈 만한 화면 쪽 상한이다. 서버가 상한을
 * 정하면 이 값 하나만 맞춘다.
 */
export const BATCH_NAME_MAX = 120

/**
 * **서버가 작업 이름을 받는가**(BR-94). 🔴 지금은 `false` — 실어 보내도 서버가 조용히 버린다(2026-10-23 실측 ·
 * 작업 `063e0575` 에 「fe-20261023-hina-flow」를 적었는데 서버 이름은 「… · 히나 시험 관리자 · 2026-10-23 09:15」였다).
 * 그래서 이름 칸은 **비활성 + 사유**로 서고 키를 싣지 않는다(eric 2026-10-23 · 팀장 지시 「서버가 받기 전까지 비활성 + 사유」).
 * ⚠ 서버 생성물 `CreateBatchBody` 에 `name` 이 들어오는 날 이 값 하나를 `true` 로 바꾸면 칸 · 본문 · 「버려졌다」 알림이 그대로 살아난다.
 */
export const BATCH_NAME_ACCEPTED = false

/** 보낼 이름 — 앞뒤 공백을 걷고, 비었으면 `undefined`(= 키를 싣지 않는다 · 서버가 조립) */
export function batchNameBody(input: string): string | undefined {
  const trimmed = input.trim()
  return trimmed === '' ? undefined : trimmed
}

/** 상한을 넘었나 — 걷은 뒤 길이로 잰다(보내는 것이 그 값이다) */
export function batchNameTooLong(input: string): boolean {
  return (batchNameBody(input)?.length ?? 0) > BATCH_NAME_MAX
}

/**
 * 서버가 이름을 버렸나. 🔴 **숨기지 않는다** — 사람이 적은 이름으로 저장된 줄 알면 나중에 못 찾는다.
 *
 * - 이름을 안 적었으면 `undefined`(비교할 것이 없다 — 자동 이름이 정상이다)
 * - 응답 이름이 보낸 것과 같으면 `undefined`(서버가 받았다)
 * - 다르면 **실제로 저장된 이름**을 돌려준다. 응답에 이름이 없으면(`null`) `null` 이다
 */
export function ignoredBatchName(
  sent: string | undefined,
  saved: string | null | undefined,
): { saved: string | null } | undefined {
  if (sent === undefined) return undefined
  if (saved === sent) return undefined
  return { saved: saved ?? null }
}
